/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { usePaymentContracts } from 'src/features/contracts/hooks/usePaymentContracts';
import { useProject } from 'src/features/projects/hooks/useProject';

import type { Contract } from 'src/types/contract.type';

import { usePaymentsOverview } from 'src/features/payments/hooks/usePaymentsOverview';
import PaymentsOverview from 'src/features/payments/components/overview/PaymentsOverview';
import ContractListForPayment from 'src/features/payments/components/ContractListForPayment';

import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

export default function PaymentsHomePage() {
    const { projectId } = useParams();

    if (!projectId) {
        return <div className="p-6 text-sm text-rose-700">잘못된 프로젝트 ID입니다.</div>;
    }

    // 권한
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditPayment = useMemo(() => !accessLoading && hasPerm(access?.permissions, 'payment.edit'), [access, accessLoading]);

    const projectQ = useProject(projectId);

    const { data: contracts = [], isLoading, error } = usePaymentContracts(projectId);

    // ✅ 표시 통화 정책: "계약 환종"
    // - 프로젝트에 contractCurrency가 있으면 그걸 우선 사용
    // - 없으면 계약 첫번째 currency fallback
    const currency = (projectQ.data as any)?.contractCurrency ?? (contracts[0] as any)?.currency ?? null;

    const overviewQ = usePaymentsOverview(projectId, currency);

    const clientContracts = useMemo(
        () => (contracts as Contract[]).filter((c) => c.direction === 'CLIENT'),
        [contracts],
    );
    const subconContracts = useMemo(
        () => (contracts as Contract[]).filter((c) => c.direction === 'SUBCON'),
        [contracts],
    );

    // UX 합의: 404/빈 목록은 조용히 빈 목록
    if (error) {
        // eslint-disable-next-line no-console
        console.error('[PaymentsHomePage] load contracts failed', error);
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Payments</h1>
                </div>
            </div>

            {/* Loading */}
            {isLoading ? (
                <div className="p-4 text-sm text-gray-500">Loading…</div>
            ) : (
                <>
                    {/* ✅ Overview (ContractsHomePage 패턴) */}
                    <PaymentsOverview
                        projectId={projectId}
                        currency={currency}
                        contracts={contracts as Contract[]}
                        overview={overviewQ.data ?? null}
                    />

                    {/* ✅ Contract list (펼침 UX + Execution 이동) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <ContractListForPayment
                            projectId={projectId}
                            title="Client Contracts"
                            contracts={clientContracts}
                            canEdit={canEditPayment}
                        />
                        <ContractListForPayment
                            projectId={projectId}
                            title="Subcon Contracts"
                            contracts={subconContracts}
                            canEdit={canEditPayment}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
